// File: /src/components/finance/FinanceDashCharts.js

import React, { useEffect, useMemo, useState } from 'react';
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	PieChart,
	Pie,
	Cell,
	Label,
} from 'recharts';
import '../../styles/finance/FinanceDashCharts.css';

const PIE_SEGMENT_COLORS = {
	income: '#2563eb',
	received: '#16a34a',
	expense: '#ef4444',
	overdue: '#f97316',
};

const formatCurrency = (value) =>
	`R$ ${Number(value).toLocaleString('pt-BR', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;

function FinanceDashCharts({ pieData, lineData, period, setPeriod }) {
	// Controle da visibilidade das linhas do gráfico de linha
	const [visibility, setVisibility] = useState({
		income: true,
		expense: true,
		paid: true,
		pending: true,
		overdue: true,
	});

	// Controle do raio do gráfico de pizza e altura do container
	const [pieRadius, setPieRadius] = useState(100);
	const [pieHeight, setPieHeight] = useState(400);
	
	useEffect(() => {
		const updatePieDimensions = () => {
			const width = window.innerWidth;
			if (width < 360) {
				setPieRadius(60);
				setPieHeight(200);
			} else if (width < 420) {
				setPieRadius(65);
				setPieHeight(220);
			} else if (width < 480) {
				setPieRadius(70);
				setPieHeight(240);
			} else if (width < 620) {
				setPieRadius(80);
				setPieHeight(260);
			} else if (width < 768) {
				setPieRadius(85);
				setPieHeight(280);
			} else {
				setPieRadius(130);
				setPieHeight(400);
			}
		};
		updatePieDimensions();
		window.addEventListener('resize', updatePieDimensions);
		return () => window.removeEventListener('resize', updatePieDimensions);
	}, []);

	// Renderiza a legenda do gráfico de linha
	const renderLegend = () => {
		const legendItems = [
			{
				value: 'Receitas',
				dataKey: 'income',
				color: '#0000FF',
				type: 'line',
			},
			{
				value: 'Despesas',
				dataKey: 'expense',
				color: '#dc3545',
				type: 'line',
			},
			{
				value: 'Faturas pagas',
				dataKey: 'paid',
				color: '#006400',
				type: 'line',
			},
			{
				value: 'Faturas pendentes',
				dataKey: 'pending',
				color: '#4B0082',
				type: 'line',
			},
			{
				value: 'Faturas atrasadas',
				dataKey: 'overdue',
				color: '#ffc107',
				type: 'line',
			},
		];

		// Renderiza os itens da legenda
		return (
			<ul className="custom-legend">
				{legendItems.map((item, index) => (
					<li
						key={`item-${index}`}
						onClick={() => handleLegendClick(item.dataKey)}
						style={{
							cursor: 'pointer',
							display: 'inline-block',
							marginRight: '20px',
							opacity: visibility[item.dataKey] ? 1 : 0.5,
						}}
					>
						<span
							style={{
								display: 'inline-block',
								width: '12px',
								height: '12px',
								marginRight: '5px',
								backgroundColor: item.color,
							}}
						/>
						{item.value}
					</li>
				))}
			</ul>
		);
	};
	// Alterna a visibilidade das linhas do gráfico de linha
	const handleLegendClick = (dataKey) => {
		setVisibility({
			...visibility,
			[dataKey]: !visibility[dataKey],
		});
	};

	// Formata os dados do gráfico de linha com ajuste de fuso horário
const formattedLineData = lineData
	.map((entry) => {
		if (!entry?.month) {
			return null;
		}

		const [yearStr, monthStr, dayStr] = entry.month.split('-');
		const year = Number(yearStr);
		const month = Number(monthStr);
		const day = dayStr ? Number(dayStr) : 1;

		if (
			!Number.isFinite(year) ||
			!Number.isFinite(month) ||
			(monthStr && !Number.isFinite(day))
		) {
			return null;
		}

		const date = new Date(year, month - 1, day);
		if (Number.isNaN(date.getTime())) {
			return null;
		}

		return {
			month: date.toISOString(),
			income:
				entry.income != null && entry.income !== ''
					? Number(entry.income)
					: null,
			expense:
				entry.expense != null && entry.expense !== ''
					? Number(entry.expense)
					: null,
			paid:
				entry.paid != null && entry.paid !== ''
					? Number(entry.paid)
					: null,
			pending:
				entry.pending != null && entry.pending !== ''
					? Number(entry.pending)
					: null,
			overdue:
				entry.overdue != null && entry.overdue !== ''
					? Number(entry.overdue)
					: null,
		};
	})
	.filter(Boolean);

	const renderCustomDot = (props) => {
		const { cx, cy, value, stroke, index } = props;
		if (value === null || value === undefined) return null;
		return (
			<circle
				key={`dot-${index}`}
				cx={cx}
				cy={cy}
				r={3}
				stroke="none"
				fill={stroke}
			/>
		);
	};

	const decorativePieData = useMemo(() => {
		const segments = (pieData || []).map((item) => ({
			...item,
			value: Number(item.value) || 0,
			color: PIE_SEGMENT_COLORS[item.key] || '#94a3b8',
		}));
		const hasValue = segments.some((segment) => segment.value > 0);
		if (hasValue) {
			return segments.filter((segment) => segment.value > 0);
		}
		return segments.slice(0, 4);
	}, [pieData]);

	const pieNetTotal = useMemo(() => {
		const income =
			decorativePieData.find((item) => item.key === 'income')?.value || 0;
		const received =
			decorativePieData.find((item) => item.key === 'received')?.value || 0;
		const expense =
			decorativePieData.find((item) => item.key === 'expense')?.value || 0;
		const overdue =
			decorativePieData.find((item) => item.key === 'overdue')?.value || 0;

		return income + received - expense - overdue;
	}, [decorativePieData]);

	const pieAbsoluteTotal = useMemo(
		() => decorativePieData.reduce((sum, item) => sum + item.value, 0),
		[decorativePieData],
	);

	const renderPieLegend = () => (
		<ul className="pie-legend">
			{decorativePieData.map((item) => (
				<li key={item.key}>
					<span
						className="pie-legend-color"
						style={{ backgroundColor: item.color }}
					/>
					<div className="pie-legend-text">
						<span className="pie-legend-name">{item.name}</span>
						<span className="pie-legend-value">
							{formatCurrency(item.value)}
						</span>
					</div>
				</li>
			))}
		</ul>
	);

	const renderPieTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const { name, value } = payload[0];
			const percentage =
				pieAbsoluteTotal > 0
					? ((value / pieAbsoluteTotal) * 100).toFixed(1)
					: '0.0';
			return (
				<div className="pie-tooltip">
					<strong>{name}</strong>
					<span>{formatCurrency(value)}</span>
					<span>{percentage}%</span>
				</div>
			);
		}
		return null;
	};

	// Renderiza o componente
	return (
		<div className="charts-container">
			{/* Gráfico de Pizza */}
			<div className="chart-box chart-box--pie">
				<h2 className="chart-title">Distribuição Financeira</h2>
				<ResponsiveContainer width="100%" height={pieHeight}>
					<PieChart>
						<Tooltip content={renderPieTooltip} />
						<Pie
							data={decorativePieData}
							dataKey="value"
							nameKey="name"
							cx="50%"
							cy="50%"
							outerRadius={pieRadius}
						>
							{decorativePieData.map((entry) => (
								<Cell
									key={`cell-${entry.key}`}
									fill={entry.color}
									stroke="none"
								/>
							))}
							<Label
								position="center"
								content={({ viewBox }) => {
									const { cx, cy } = viewBox;
									return (
										<g>
											<text
												x={cx}
												y={cy - 8}
												textAnchor="middle"
												className="pie-label-total"
											>
												Saldo Efetivo
											</text>
											<text
												x={cx}
												y={cy + 14}
												textAnchor="middle"
												className="pie-label-value"
											>
												{formatCurrency(pieNetTotal)}
											</text>
										</g>
									);
								}}
							/>
						</Pie>
					</PieChart>
				</ResponsiveContainer>
				{renderPieLegend()}
			</div>

			{/* Gráfico de Linha */}
			<div className="chart-box chart-box--line">
				<div className="chart-box__header chart-header">
					<h2 className="chart-title">Movimentação Financeira</h2>
					<div className="chart-period-select">
						<label>Visualização:</label>
						<select
							value={period}
							onChange={(e) => setPeriod(e.target.value)}
						>
							<option value="monthly">Mensal</option>
							<option value="annual">Anual</option>
						</select>
					</div>
				</div>

				<div className="chart-box__legend chart-box__legend--line">
					{renderLegend()}
				</div>

				<div className="chart-box__chart chart-box__chart--line">
					<ResponsiveContainer width="100%" height="100%">
						<LineChart
							data={formattedLineData}
							margin={{ left: -20, right: 20, top: 6, bottom: 0 }}
						>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis
								dataKey="month"
								tickFormatter={(tick) => {
								const date = new Date(tick);
								// Ajusta para o fuso horário local
								const localDate = new Date(
									date.getTime() +
										date.getTimezoneOffset() * 60000
								);
								const day = ('0' + localDate.getDate()).slice(
									-2
								);
								const month = (
									'0' +
									(localDate.getMonth() + 1)
								).slice(-2);
								const year = localDate.getFullYear();
								return `${day}-${month}-${year}`;
							}}
							tick={{ fontSize: '11px' }}
						/>
						<YAxis tick={{ fontSize: '11px' }} />
						<Tooltip
							formatter={(value) =>
								`R$ ${Number(value).toLocaleString('pt-BR', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}`
							}
							labelFormatter={(label) => {
								const date = new Date(label);
								// Ajusta para o fuso horário local
								const localDate = new Date(
									date.getTime() +
										date.getTimezoneOffset() * 60000
								);
								const day = ('0' + localDate.getDate()).slice(
									-2
								);
								const month = (
									'0' +
									(localDate.getMonth() + 1)
								).slice(-2);
								const year = localDate.getFullYear();
								return `${day}-${month}-${year}`;
							}}
							contentStyle={{
								fontSize: '11px',
								backgroundColor: 'rgba(255, 255, 255, 0.95)',
								border: '1px solid #ccc',
								borderRadius: '4px',
								padding: '10px',
								boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
							}}
							itemStyle={{
								color: '#333',
								fontSize: '11px',
							}}
							labelStyle={{
								fontSize: '14px',
								fontWeight: 'bold',
							}}
						/>
						{visibility.income && (
							<Line
								key="income-line"
								type="monotone"
								dataKey="income"
								stroke="#0000FF"
								strokeWidth={2}
								name="Receitas"
								dot={renderCustomDot}
							/>
						)}
						{visibility.expense && (
							<Line
								key="expense-line"
								type="monotone"
								dataKey="expense"
								stroke="#dc3545"
								strokeWidth={2}
								name="Despesas"
								dot={renderCustomDot}
							/>
						)}
						{visibility.paid && (
							<Line
								key="paid-line"
								type="monotone"
								dataKey="paid"
								stroke="#006400"
								strokeWidth={2}
								name="Faturas pagas"
								dot={renderCustomDot}
							/>
						)}
						{visibility.pending && (
							<Line
								key="pending-line"
								type="monotone"
								dataKey="pending"
								stroke="#4B0082"
								strokeWidth={2}
								name="Faturas pendentes"
								dot={renderCustomDot}
							/>
						)}
						{visibility.overdue && (
							<Line
								key="overdue-line"
								type="monotone"
								dataKey="overdue"
								stroke="#ffc107"
								strokeWidth={2}
								name="Faturas atrasadas"
								dot={renderCustomDot}
							/>
						)}
						</LineChart>
					</ResponsiveContainer>
				</div>
			</div>
		</div>
	);
}

export default FinanceDashCharts;
